import React from 'react';
import { Input, Button, Select, Modal, DatePicker, Tooltip } from 'components';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants, checkPermission } from 'utils';
import { AccreditationService } from 'services';
import moment from 'moment';
import { ManualIcon, CheckGreenCircle } from 'assets/icons';

interface Props {
	onClose?: any;
	item?: any;
}

const INITIAL_STATE = {
	date: '',
	reference: '',
	accreditation_reference: '',
	accreditation_amount: ''
}

class ModalView extends React.Component<Props> {

	state = {
		san: null,
		accreditations: [],
		payments: [],
		current_date: undefined,
		visible_payment: false,
		due_number: null,
		position: null,
		form: INITIAL_STATE,
		visible_manual_accreditation: false
	}

	componentDidMount() {
		this.load();
	}

	load = async (withoutLoading = false) => {
		const res: any = await AccreditationService.admin.view({
			san_id: this.props.item?.id,
			withoutLoading
		});
		this.setState({
			san: res.san,
			accreditations: res.accreditations || [],
			payments: res.payments || [],
			current_date: res.current_date || undefined
		});
	}

	accreditation = (item: any) => {
		Globals.confirm('¿Desea acreditar al usuario?',async () => {
			try {
				await AccreditationService.admin.save({
					san_id: this.props.item?.id,
					user_id: item?.user_id,
					position_id: item?.id
				});
				Globals.showSuccess("Se ha acreditado al usuario correctamente");
				this.load(true);
			}
			catch(e) {
				this.load(true);
			}
		});
	}

	checkPayment = (due_number: number, date: string, user_id: number) => {
		let name = '';
		const san: any = this.state.san;
		const position = san?.positions.find((i: any) => i.user_id == user_id);
		const is_same_position = position?.position == due_number && san?.modality_id == Constants.SAN_MODALITIES.NEW_MODEL;
		const paid = this.state.payments.find((i: any) => i.due_number == due_number && i.user_id == user_id) != null || is_same_position;

		const is_pending = moment(this.state.current_date).format('YYYY-MM-DD') > moment(date).format('YYYY-MM-DD') && !paid;

		if (is_pending) {
			name = 'td-gray';
		}
		else if (paid) {
			name = 'td-green';
		}

		return name;
	}

	isPaid = (due_number: number, date: string, user_id: number) => {
		const san: any = this.state.san;
		const position = san?.positions.find((i: any) => i.user_id == user_id);
		const is_same_position = position?.position == due_number && san?.modality_id == Constants.SAN_MODALITIES.NEW_MODEL;
		const paid = this.state.payments.find((i: any) => i.due_number == due_number && i.user_id == user_id) || is_same_position;

		if (paid) {
			return paid;
		}

		return false;
	}

	isPreviewPaid = (due_number: number, date: string, user_id: number) => {
		const san: any = this.state.san;
		const position = san?.positions.find((i: any) => i.user_id == user_id);
		let paid = true;

		for (let j = due_number; j > 0; j--) {
			const is_same_position = position?.position == j && san?.modality_id == Constants.SAN_MODALITIES.NEW_MODEL;
			const payment = this.state.payments.find((i: any) => i.due_number == j && i.user_id == user_id) != null || is_same_position;
			if (!payment) {
				paid = false;
			}
		}

		return paid;
	}

	getStatus = (due_number: number) => {
		let name = 'Pendiente';

		const payments = this.state.payments.filter((i: any) => i.due_number == due_number);

		// @ts-ignore
		if (payments.length >= this.state.san?.positions?.length) {
			name = "Completo";
		}

		return name;
	}

	getDate = (due_number: number) => {
		// @ts-ignore
		return moment(this.state.san?.start_date).add(Globals.convertFrequencyQuantity(due_number - 1,this.state.san),Globals.convertFrequency(this.state.san?.frequency)).format('DD/MM/YYYY')
	}

	setPayment = (position: any, due_number: number) => {
		this.setState({
			visible_payment: true,
			position,
			due_number
		},() => {
			this.loadDueData();
		});
	}

	loadDueData = async () => {
		const position: any = this.state.position;
		const san: any = this.state.san;

		const res: any = await AccreditationService.admin.getPaymentData({
			due_number: this.state.due_number,
			user_id: position?.user_id,
			san_id: position?.san_id
		});
		this.setState({
			form: {
				...this.state.form,
				reference: `Ajuste-Cuota${ this.state.due_number }-${ moment().format('DDMMYYYY') }`,
				date: moment().toDate(),
				amount: san?.dues || '',
				commission: parseFloat((san?.dues * ((res.commission_percentage || 0) / 100)).toFixed(2))
			}
		});
	}

	savePayment = () => {
		const san: any = this.state.san;
		const position: any = this.state.position;

		const { date, reference } = this.state.form;

		if (!date || !reference) {
			Globals.showError("Por favor, ingrese todos los datos para continuar");
			return;
		}

		Globals.confirm(`¿Desea marcar la cuota ${ this.state.due_number } del usuario ${ position?.user?.name + ' ' + position?.user?.lastname } en el san ${ san?.code } como pagada? Esta acción no se puede deshacer`,async () => {
			await AccreditationService.admin.setPayment({
				due_number: this.state.due_number,
				user_id: position?.user_id,
				san_id: position?.san_id,
				date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
				reference: this.state.form.reference
			});
			Globals.showSuccess("Se ha guardado la cuota correctamente");
			this.load(true);
			this.setState({
				visible_payment: false,
				due_number: null,
				position: null,
				form: INITIAL_STATE
			});
		});
	}

	saveManualAccreditation = () => {
		const san: any = this.state.san;
		const position: any = this.state.position;

		const { accreditation_reference, accreditation_amount } = this.state.form;

		if (!accreditation_reference || !accreditation_amount) {
			Globals.showError("Por favor, ingrese todos los datos para continuar");
			return;
		}

		Globals.confirm(`¿Desea marcar como acreditado al usuario ${ position?.user?.name + ' ' + position?.user?.lastname } en el san ${ san?.code }? Esta acción no se puede deshacer`,async () => {
			await AccreditationService.admin.saveManualAccreditation({
				user_id: position?.user_id,
				san_id: position?.san_id,
				amount: this.state.form.accreditation_amount,
				reference: this.state.form.accreditation_reference
			});
			Globals.showSuccess("Se ha guardado la acreditación correctamente");
			this.load(true);
			this.setState({
				visible_manual_accreditation: false,
				position: null,
				form: INITIAL_STATE
			});
		});
	}

	onClosePayment = () => {
		this.setState({
			visible_payment: false,
			due_number: null,
			position: null,
			form: INITIAL_STATE,
			visible_manual_accreditation: false
		});
	}

	change = (e: any,callback = () => {}) => {
		this.setState({
			page: 1,
			form: {
				...this.state.form,
				[e.target.name]: e.target.value
			}
		},callback);
	}
	
	render() {
		const san: any = this.state.san;
		const show_button = san?.positions?.filter((i: any) => !i.position).length == 0;
		const { visible_payment, visible_manual_accreditation } = this.state;

		return (
			<>
				<Modal
		          className="modal-set-payment"
		          visible={ visible_payment }
		          title="Marcar cuota"
		          onClose={ () => this.onClosePayment() }
		        >
		          <div id="modal-set-payment">
		          	<div className="row">
		          		<div className="col-md-6">
		          			<DatePicker
								label="Fecha de pago"
								placeholder="Fecha"
								maxDate={ moment().toDate() }
								onChange={ (text: string) => {
									this.change({
										target: {
											value: text,
											name: 'date'
										}
									},() => this.load(true));
								} }
								value={ this.state.form.date }
							/>
		          		</div>
		          		<div className="col-md-6">
		          			<Input
								value={ this.state.form.reference }
								name="reference"
								placeholder="Referencia"
								label="Nº de Referencia"
								onChange={ (e: any) => this.change(e) } />
		          		</div>
		          		<div className="col-md-12">
		          			<div className="text-center">
								<Button type="submit" className="btn-save" onClick={ () => this.savePayment() }>
									Guardar
								</Button>
							</div>
		          		</div>
		          	</div>
		          </div>
		      	</Modal>

		      	<Modal
		          className="modal-set-payment"
		          visible={ visible_manual_accreditation }
		          title="Marcar usuario como acreditado"
		          onClose={ () => this.onClosePayment() }
		        >
		          <div id="modal-set-payment">
		          	<p className="warning">Importante: Esta acción no genera ningún pago. Emplea esta función solo si estás registrando una acreditación manual externa.</p>
		          	<div className="row">
		          		<div className="col-md-6">
		          			<Input
								value={ this.state.form.accreditation_reference }
								name="accreditation_reference"
								placeholder="Referencia"
								label="Nº de Referencia"
								onChange={ (e: any) => this.change(e) } />
		          		</div>
		          		<div className="col-md-6">
		          			<Input
								value={ this.state.form.accreditation_amount }
								name="accreditation_amount"
								placeholder="Monto pagado"
								label="Monto pagado"
								type="decimal"
								onChange={ (e: any) => this.change(e) } />
		          		</div>
		          		<div className="col-md-12">
		          			<div className="text-center">
								<Button type="submit" className="btn-save" onClick={ () => this.saveManualAccreditation() }>
									Guardar
								</Button>
							</div>
		          		</div>
		          	</div>
		          </div>
		      	</Modal>

				<div id="modal-view-accreditations">
					<div className="container-header">
						<p className="title">Balance de pagos</p>
						<p className="subtitle"><strong>Estatus:</strong> { san?.status_payment_text }</p>
						<p className="subtitle">San: { san?.code }</p>
						<div className="container-leyend">
							<img src={ ManualIcon } />
							<p>Cuota manual</p>
						</div>
					</div>
					<div className="row">
						<div className="col-2">
							<p className="header">Participantes</p>
						</div>
						<div className="col-8">
							<p className="header">Pagos cuotas { san?.frequency == Constants.FREQUENCIES.WEEK ? 'semanales' : san?.frequency == Constants.FREQUENCIES.MONTH ? 'mensuales' : 'quincenales' }</p>
						</div>
						<div className="col-2">
							<p className="header" style={{ minHeight: '29.5px' }}>{ san?.modality_id == Constants.SAN_MODALITIES.CLASSIC ? 'Acreditar' : '' }</p>
						</div>
						<div className="col-2 bg-gray">
							<p className="bold" style={{ fontSize: '12px' }}>Nombre</p>
						</div>
						<div className="col-8 bg-gray">
							<table style={{ width: '100%' }}>
								<tr>
									{
										[...Array(Math.ceil(san?.amount / san?.dues) || 0)].map((item: number, index: number) => {
											return (
												<td className="td-week" style={{ width: ([...Array(Math.ceil(san?.amount / san?.dues) || 0)].length / 100) + '%' }}>{ index + 1 }</td>
											)
										})
									}
								</tr>
							</table>
						</div>
						<div className="col-2 bg-gray"></div>
						{
							san?.positions?.map((item: any, index: number) => {
								const accreditation = this.state.accreditations.find((i: any) => i.user_id == item.user_id);
								let is_the_day = moment(this.state.current_date).format('YYYY-MM-DD') >= moment(san?.start_date).add(Globals.convertFrequencyQuantity(item.position - 1,san),Globals.convertFrequency(san.frequency)).format('YYYY-MM-DD');

								if (san?.modality_id == Constants.SAN_MODALITIES.NEW_MODEL) {
									is_the_day = moment(this.state.current_date).format('YYYY-MM-DD') > moment(san?.start_date).add(Globals.convertFrequencyQuantity(item.position - 1,san),Globals.convertFrequency(san.frequency)).format('YYYY-MM-DD');
								}

								return (
									<>
										<div className="col-2 bg-gray">
											<p className="bold name">{ item?.position }º { item?.user?.name + ' ' + item?.user?.lastname }</p>
										</div>
										<div className="col-8 bg-gray">
											<table style={{ width: '100%' }}>
												<tr>
													{
														[...Array(Math.ceil(san?.amount / san?.dues) || 0)].map((i: number, index: number) => {
															const date = moment(san.start_date).add(Globals.convertFrequencyQuantity(index,san),Globals.convertFrequency(san.frequency)).format('YYYY-MM-DD')

															return (
																<td onClick={ () => {
																	if (checkPermission(Constants.MODULES.ACCREDITATIONS,'write') && this.isPreviewPaid(index,date,item.user_id) && !this.isPaid(index + 1,date,item.user_id)) {
																		this.setPayment(item,index + 1);
																	}
																} } className={ `td-value ${ this.checkPayment(index + 1,date,item.user_id) } ${ this.isPreviewPaid(index,date,item.user_id) && checkPermission(Constants.MODULES.ACCREDITATIONS,'write') && !this.isPaid(index + 1,date,item.user_id) ? 'active' : '' }` } style={{ width: ([...Array(Math.ceil(san?.amount / san?.dues) || 0)].length / 100) + '%' }}>
																	{ Globals.formatMiles(san?.dues,true,'') }
																	{
																		// @ts-ignore
																		this.isPaid(index + 1,date,item.user_id)?.manual_due && (
																			<img src={ ManualIcon } className="manual-icon" />
																		)
																	}
																</td>
															)
														})
													}
												</tr>
											</table>
										</div>
										<div className="col-2 bg-gray">
											<div style={{ display: 'flex', alignItems: 'center' }}>
												{
													san?.start_date != null && (
														<>
															{
																san.currency_id != Constants.CURRENCIES.COP && checkPermission(Constants.MODULES.ACCREDITATIONS,'write') && !item.pending_to_pay && show_button && is_the_day && accreditation == null &&  (
																	<button className="btn btn-valid" onClick={ () => this.accreditation(item) }>
																		Acreditar
																	</button>
																)
															}

															{
																accreditation != null && (
																	<p className="accreditation">Acreditado</p>
																)
															}

															{
																checkPermission(Constants.MODULES.ACCREDITATIONS,'write') && !item.pending_to_pay && show_button && is_the_day && accreditation == null && item.fail_accreditation == Constants.FAIL_ACCREDITATION.YES && (
																	<div className="fail-accreditation-container">
																		<p className="danger">Fallo al acreditar</p>
																		<Tooltip title="Marcar como acreditado">
																			<a className="link-icon" href="#" onClick={ (e: any) => {
																				e.preventDefault();
																				this.setState({ visible_manual_accreditation: true, position: item });
																			}}>
																				<img src={ CheckGreenCircle } />
																			</a>
																		</Tooltip>
																	</div>
																)
															}
														</>
													)
												}
											</div>
										</div>
									</>
								)
							})
						}
						<div className="col-2 bg-cyan">
							<p className="bold">Estatus</p>
						</div>
						<div className="col-8 bg-cyan">
							<table style={{ width: '100%' }}>
								<tr>
									{
										[...Array(Math.ceil(san?.amount / san?.dues) || 0)].map((item: number, index: number) => {
											return (
												<td className="td-week" style={{ width: ([...Array(Math.ceil(san?.amount / san?.dues) || 0)].length / 100) + '%' }}>
													<p className="status-payment" style={{ marginBottom: san?.start_date == null ? '5px' : '0px' }}>{ this.getStatus(index + 1) }</p>
													{ san?.start_date != null && <p className="status-payment">{ this.getDate(index + 1) }</p> }
												</td>
											)
										})
									}
								</tr>
							</table>
						</div>
						<div className="col-2 bg-cyan"></div>
					</div>
				</div>
			</>
		)
	}
}

export default ModalView;